import { CardElement, ColorRank, PlayStyle } from "../types/battle"
import {
  AlmanacCell,
  AlmanacProgressSummary,
  AlmanacSpecies,
  AlmanacStampEvent,
  AlmanacView,
  PublicAlmanacView,
} from "../types/almanac"
import { AFFILIATION_ELEMENT_TITLES } from "../constants/affiliationInfo"
import { STYLE_NAMES } from "../constants/combatMatchups"
import { RANK_NAMES } from "../constants/guardians"
import { RANK_ORDER } from "../constants/game.constants"
import { normalizeCardElement } from "./cardElement"

const ELEMENTS: CardElement[] = [
  "FIRE",
  "WATER",
  "EARTH",
  "LIGHTNING",
  "WIND",
  "ICE",
]

const STYLES: PlayStyle[] = ["ATTACK", "DEFENSE", "SPEED"]

const COLOR_RANKS: ColorRank[] = [...RANK_ORDER]

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1" || value === "true") return true
  if (value === 0 || value === "0" || value === "false") return false
  return fallback
}

function unwrapPayload(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as Record<string, unknown>
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>
  }
  return obj
}

function pickEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback?: T
): T | null {
  const raw = pickString(value).toUpperCase() as T
  if (allowed.includes(raw)) return raw
  return fallback ?? null
}

function pickPlayStyle(value: unknown): PlayStyle | null {
  return pickEnum(value, STYLES)
}

function pickColorRank(value: unknown): ColorRank | null {
  return pickEnum(value, COLOR_RANKS)
}

function formatRankTitle(rank: ColorRank): string {
  const name = RANK_NAMES[rank] ?? rank
  return name.charAt(0) + name.slice(1).toLowerCase()
}

export function almanacSlotKey(
  element: CardElement,
  style: PlayStyle,
  colorRank: ColorRank
): string {
  return `${element}:${style}:${colorRank}`
}

function normalizeAlmanacCell(raw: unknown): AlmanacCell | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const element = normalizeCardElement(obj.element)
  const style = pickPlayStyle(obj.style)
  const colorRank = pickColorRank(obj.colorRank ?? obj.color_rank)
  if (!element || !style || !colorRank) return null

  const stampedAtRaw = obj.stampedAt ?? obj.stamped_at
  const stampedAt =
    stampedAtRaw == null || stampedAtRaw === ""
      ? null
      : pickString(stampedAtRaw) || null

  const unlocked = pickBool(obj.unlocked, stampedAt != null)
  const label =
    pickString(obj.label) ||
    `${AFFILIATION_ELEMENT_TITLES[element]} · ${STYLE_NAMES[style]} · ${formatRankTitle(colorRank)}`

  return {
    element,
    style,
    colorRank,
    unlocked,
    stampedAt,
    label,
  }
}

function normalizeAlmanacSpecies(raw: unknown): AlmanacSpecies | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const element = normalizeCardElement(obj.element)
  const style = pickPlayStyle(obj.style)
  if (!element || !style) return null

  const ranksRaw = Array.isArray(obj.ranks) ? obj.ranks : []
  const ranks = ranksRaw
    .map((raw) => {
      const record =
        raw && typeof raw === "object" && !Array.isArray(raw)
          ? (raw as Record<string, unknown>)
          : {}
      return normalizeAlmanacCell({
        ...record,
        element: record.element ?? element,
        style: record.style ?? style,
      })
    })
    .filter((cell): cell is AlmanacCell => cell != null)
    .sort(
      (a, b) =>
        COLOR_RANKS.indexOf(a.colorRank) - COLOR_RANKS.indexOf(b.colorRank)
    )

  const unlockedRanks = pickNumber(
    obj.unlockedRanks ?? obj.unlocked_ranks,
    ranks.filter((r) => r.unlocked).length
  )
  const totalRanks = pickNumber(obj.totalRanks ?? obj.total_ranks, 6)

  return {
    element,
    style,
    elementTitle:
      pickString(obj.elementTitle ?? obj.element_title) ||
      AFFILIATION_ELEMENT_TITLES[element],
    styleTitle:
      pickString(obj.styleTitle ?? obj.style_title) || STYLE_NAMES[style],
    unlockedRanks,
    totalRanks,
    ranks,
  }
}

function buildSpeciesFromCells(cells: AlmanacCell[]): AlmanacSpecies[] {
  const byKey = new Map<string, AlmanacCell[]>()
  for (const cell of cells) {
    const key = `${cell.element}:${cell.style}`
    const list = byKey.get(key) ?? []
    list.push(cell)
    byKey.set(key, list)
  }

  const species: AlmanacSpecies[] = []
  for (const element of ELEMENTS) {
    for (const style of STYLES) {
      const ranks = (byKey.get(`${element}:${style}`) ?? [])
        .slice()
        .sort(
          (a, b) =>
            COLOR_RANKS.indexOf(a.colorRank) - COLOR_RANKS.indexOf(b.colorRank)
        )
      species.push({
        element,
        style,
        elementTitle: AFFILIATION_ELEMENT_TITLES[element],
        styleTitle: STYLE_NAMES[style],
        unlockedRanks: ranks.filter((r) => r.unlocked).length,
        totalRanks: 6,
        ranks,
      })
    }
  }
  return species
}

export function normalizeAlmanacView(data: unknown): AlmanacView {
  const obj = unwrapPayload(data)

  const cellsRaw = Array.isArray(obj.cells) ? obj.cells : []
  const cells = cellsRaw
    .map(normalizeAlmanacCell)
    .filter((cell): cell is AlmanacCell => cell != null)

  const speciesRaw = Array.isArray(obj.species) ? obj.species : []
  let species = speciesRaw
    .map(normalizeAlmanacSpecies)
    .filter((item): item is AlmanacSpecies => item != null)

  if (species.length === 0 && cells.length > 0) {
    species = buildSpeciesFromCells(cells)
  }

  const stampedSlots = pickNumber(
    obj.stampedSlots ?? obj.stamped_slots,
    cells.filter((c) => c.unlocked).length
  )
  const totalSlots = pickNumber(obj.totalSlots ?? obj.total_slots, 108)
  const progressPercent = pickNumber(
    obj.progressPercent ?? obj.progress_percent,
    totalSlots > 0
      ? Math.round((stampedSlots / totalSlots) * 1000) / 10
      : 0
  )

  return {
    title:
      pickString(obj.title) || "Великий Альманах Стихий",
    description:
      pickString(obj.description) ||
      "Журнал комбинаций стихия × стиль × ранг, которые вы когда-либо получали.",
    stampedSlots,
    totalSlots,
    progressPercent,
    species,
    cells,
  }
}

export function normalizePublicAlmanacView(data: unknown): PublicAlmanacView {
  const obj = unwrapPayload(data)
  const view = normalizeAlmanacView(data)
  return {
    ...view,
    nickname: pickString(obj.nickname, "Хранитель"),
    publicGameCode: pickString(
      obj.publicGameCode ?? obj.public_game_code
    ),
  }
}

export function normalizeAlmanacProgressSummary(
  raw: unknown
): AlmanacProgressSummary | null {
  if (raw == null) return null

  if (typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>

  const nested = obj.progress ?? obj.summary
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const fromNested = normalizeAlmanacProgressSummary(nested)
    if (fromNested) return fromNested
  }

  if (Array.isArray(obj.cells) || Array.isArray(obj.species) || obj.title != null) {
    const view = normalizeAlmanacView(raw)
    return {
      stampedSlots: view.stampedSlots,
      totalSlots: view.totalSlots,
    }
  }

  const stampedRaw =
    obj.stampedSlots ??
    obj.stamped_slots ??
    obj.openedSlots ??
    obj.opened_slots ??
    obj.unlockedSlots ??
    obj.unlocked_slots

  if (stampedRaw == null || stampedRaw === "") return null

  const stampedSlots = pickNumber(stampedRaw)
  if (!Number.isFinite(stampedSlots) || stampedSlots < 0) return null

  const totalRaw = obj.totalSlots ?? obj.total_slots ?? obj.maxSlots ?? obj.max_slots
  const totalSlots =
    totalRaw == null || totalRaw === ""
      ? 108
      : pickNumber(totalRaw, 108)

  return {
    stampedSlots,
    totalSlots: totalSlots > 0 ? totalSlots : 108,
  }
}

/** Прогресс альманаха из ответа GET /game/passport/public/:code */
export function resolvePublicProfileAlmanacProgress(
  obj: Record<string, unknown>
): AlmanacProgressSummary | null {
  const candidates: unknown[] = [
    obj.almanac,
    obj.almanacProgress ?? obj.almanac_progress,
    {
      stampedSlots:
        obj.almanacStampedSlots ??
        obj.almanac_stamped_slots ??
        obj.stampedSlots ??
        obj.stamped_slots,
      totalSlots:
        obj.almanacTotalSlots ??
        obj.almanac_total_slots ??
        obj.totalSlots ??
        obj.total_slots,
    },
  ]

  for (const candidate of candidates) {
    const parsed = normalizeAlmanacProgressSummary(candidate)
    if (parsed) return parsed
  }

  return null
}

function normalizeAlmanacStampEvent(raw: unknown): AlmanacStampEvent | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const element = normalizeCardElement(obj.element)
  const style = pickPlayStyle(obj.style)
  const colorRank = pickColorRank(obj.colorRank ?? obj.color_rank)
  if (!element || !style || !colorRank) return null

  const stampedAt = pickString(obj.stampedAt ?? obj.stamped_at)
  if (!stampedAt) return null

  const stampedSlots = pickNumber(obj.stampedSlots ?? obj.stamped_slots)
  const totalSlots = pickNumber(obj.totalSlots ?? obj.total_slots, 108)

  const modalRaw =
    obj.modal && typeof obj.modal === "object" && !Array.isArray(obj.modal)
      ? (obj.modal as Record<string, unknown>)
      : {}

  const elementTitle =
    pickString(modalRaw.elementTitle ?? modalRaw.element_title) ||
    AFFILIATION_ELEMENT_TITLES[element]
  const styleTitle =
    pickString(modalRaw.styleTitle ?? modalRaw.style_title) ||
    STYLE_NAMES[style]
  const rankTitle =
    pickString(modalRaw.rankTitle ?? modalRaw.rank_title) ||
    formatRankTitle(colorRank)

  return {
    isNew: true,
    element,
    style,
    colorRank,
    stampedAt,
    stampedSlots,
    totalSlots,
    modal: {
      title:
        pickString(modalRaw.title) || "Новая запись в Альманахе",
      subtitle:
        pickString(modalRaw.subtitle) ||
        `${elementTitle} · ${styleTitle} · ${rankTitle}`,
      message:
        pickString(modalRaw.message) ||
        `Открыто ${stampedSlots} из ${totalSlots}`,
      elementTitle,
      styleTitle,
      rankTitle,
    },
  }
}

/** Собирает штампы из ответа экшена (almanacStamps + almanacStamp). */
export function collectAlmanacStamps(res: unknown): AlmanacStampEvent[] {
  if (!res || typeof res !== "object" || Array.isArray(res)) return []
  const obj = unwrapPayload(res)
  const fromArray = Array.isArray(obj.almanacStamps)
    ? obj.almanacStamps
    : Array.isArray(obj.almanac_stamps)
      ? obj.almanac_stamps
      : []
  const single = obj.almanacStamp ?? obj.almanac_stamp

  const stamps: AlmanacStampEvent[] = [
    ...fromArray
      .map(normalizeAlmanacStampEvent)
      .filter((s): s is AlmanacStampEvent => s != null),
  ]

  const one = normalizeAlmanacStampEvent(single)
  if (one) {
    const key = almanacSlotKey(one.element, one.style, one.colorRank)
    if (
      !stamps.some(
        (s) => almanacSlotKey(s.element, s.style, s.colorRank) === key
      )
    ) {
      stamps.push(one)
    }
  }

  return stamps
}
